/**
 * @typedef {import("./services/state.js").SelectedLocation} SelectedLocation
 * @typedef {import("./components/reserve-button/reserve-button.js").default} ReserveButton
 * @typedef {import("./components/location-button/location-button.js").default} LocationButton
 */

import { batch, effect, signal } from "@preact/signals";
import { cacheLocation, createAppState } from "./services/state";
import { shouldSuppressEscape, suppressEscapeFor } from "./services/escape-key-guard";
import { registerPopupInstance } from "./services/popup-escape-guard";
import { createOrdersApp } from "./modules/orders/main";
import { createReservationApp } from "./modules/reservations/main";
import { fetchRestaurants } from "./services/wordpress-api";
import "./components/location-button";
import "./components/reserve-button";
import "./components/order-button/order-button.js";
import "./components/select/select.js";
import "./components/spinner/spinner.js";
import "./modules/restaurants/main.js";

// const HARD_CODED_RESTAURANT_ID = 2928;

/** Must match the STORAGE_KEY used by the WordPress saveLocation() function */
const SESSION_STORAGE_KEY = "session_location";

// ─── Session Storage Helpers ──────────────────────────────────────────────────

/**
 * Patch sessionStorage.setItem once so any same-tab write dispatches
 * a "session-storage-updated" CustomEvent on window.
 * Safe to call multiple times — only patches once.
 */
function patchSessionStorage() {
  if (window.__sessionStoragePatched) return;
  const original = sessionStorage.setItem.bind(sessionStorage);
  sessionStorage.setItem = function (key, value) {
    original(key, value);
    window.dispatchEvent(
      new CustomEvent("session-storage-updated", { detail: { key, value } })
    );
  };
  window.__sessionStoragePatched = true;
}
/**
 * Parse a raw JSON string (from sessionStorage) into a SelectedLocation.
 * Returns null if the string is missing or lacks required fields.
 * @param {string | null} raw
 * @returns {SelectedLocation | null}
 */
function parseSessionLocation(raw) {
  try {
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.postId || !parsed?.slug || !parsed?.name) return null;
    return {
      id: Number(parsed.postId),
      title: parsed.name,
      slug: parsed.slug,
    };
  } catch {
    return null;
  }
}

/**
 * Reads the current session_location from sessionStorage.
 * @returns {SelectedLocation | null}
 */
function getSessionLocation() {
  return parseSessionLocation(sessionStorage.getItem(SESSION_STORAGE_KEY));
}

// ─── Bootstrap ───────────────────────────────────────────────────────────────

patchSessionStorage();

const sessionLoc = getSessionLocation();
let state = createAppState(sessionLoc);

/**
 * Listen for location changes dispatched by the WordPress saveLocation() function.
 */
window.addEventListener("sessionLocationChange", (e) => {
    const loc = parseSessionLocation(JSON.stringify(e.detail));
    if (loc) {
      cacheLocation(loc);
      state.selectedLocation.value = loc;
    }
  });
// ─── Signals & Effects ───────────────────────────────────────────────────────

let hasInitialized = false;

// Global location signal for external updates
const externalLocation = signal(null);

/**
 * Setup external location listener
 */
effect(() => {
  // @ts-ignore
  window.reservationState = {
    onLocationChange: (location) => {
      externalLocation.value = location;
    },
  };
  return () => {
    // @ts-ignore
    window.reservationState = null;
  };
});

/** Countdown timer for pending reservations */
effect(() => {
    if (!state.pendingTimer.value) {
        return;
    }

    const interval = setInterval(() => {
        state.timerTick.value++;

        if (state.pendingTimer.value.getTime() <= Date.now()) {
            clearInterval(interval);

            batch(() => {
                state.pendingTimer.value = null;
                state.isExpired.value = true;
            });
        }
    }, 1000);

    return () => clearInterval(interval);
});

/** Update app state when external location changes */
effect(() => {
  if (externalLocation.value) {
    const payload = {
      id: externalLocation.value.id,
      title: externalLocation.value.title,
      slug: externalLocation.value.slug,
    };
    cacheLocation(payload);
    state.selectedLocation.value = payload;
  }
});


effect(() => {
    const loc = state.selectedLocation.value;
  
    if (!loc) {
      document.body.classList.add("no-location");
  
      // Optional: fire a global event
      window.dispatchEvent(new CustomEvent("no-location-detected"));
    } else {
      document.body.classList.remove("no-location");
    }
  });
// ─── Elementor Popup ─────────────────────────────────────────────────────────

/**
 * Handle Elementor popup show event.
 * Reinitializes reservation and order components when a popup is displayed.
 */
function handlePopupShow(event, id, instance) {
  const popupElement = document.querySelector(`#elementor-popup-modal-${id}`);

  registerPopupInstance(id, instance);

  if (popupElement) {
    popupElement.querySelectorAll("mx-reservation").forEach((el) => createReservationApp(state, el));
    popupElement.querySelectorAll("mx-order").forEach((el) => createOrdersApp(state, el));
  }
}

// ─── Main ─────────────────────────────────────────────────────────────────────

async function main() {
  if (hasInitialized) {
    return;
  }
  hasInitialized = true;

  const currentPage = new URL(location.href);
  const page = document.querySelector("#restaurant-page");
  const displayParam = currentPage.searchParams.get("display");

  if (page && (displayParam === "reservation" || displayParam === "order")) {
    const ridClass = Array.from(page.classList.values()).find((c) =>
      /rid-\d{1,5}/.test(c)
    );
    if (ridClass) {
      state.selectedRid.value = ridClass.replace("rid-", "");
    }
  }

  document.querySelectorAll("mx-reservation").forEach((el) => createReservationApp(state, el));
  document.querySelectorAll("mx-order").forEach((el) => createOrdersApp(state, el));

  // Add highest-priority Escape blocker for calendar
  // This prevents Elementor from closing the popup when calendar has focus
  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key === "Escape") {
        if (shouldSuppressEscape()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        const focusedElement = document.activeElement;
        /** @type {(HTMLElement  & { closeCalendar?: () => void }) | null} */
        const calendar = document.querySelector("mx-reservation-calendar");
        const isCalendarOpen =
          calendar &&
          calendar.getAttribute("aria-hidden") !== "true" &&
          calendar.style.display !== "none";
        
        if (isCalendarOpen) {
          // Calendar has focus - block Escape from closing the popup
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (typeof calendar.closeCalendar === "function") {
            calendar.closeCalendar();
          } else {
            suppressEscapeFor();
          }
        }
      }
    },
    true // Capture phase - fires BEFORE other handlers
  );

  document.addEventListener(
    "keyup",
    (event) => {
      if (event.key === "Escape" && shouldSuppressEscape()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  jQuery(document).on("elementor/popup/show", handlePopupShow);

  if (state.restaurants.value.length === 0 && !state.isFetchingRestaurants.value) {
    state.isFetchingRestaurants.value = true;
    await fetchRestaurants()
      .then((json) => {
        batch(() => {
          state.restaurants.value = json;
          state.isFetchingRestaurants.value = false;
        });

        // Only apply a fallback if neither session storage nor an external
        // location has already seeded the selected location.
        // if (!state.selectedLocation.value && !externalLocation.value) {
        //   const hardCoded = json.find((r) => r.id === HARD_CODED_RESTAURANT_ID);
        //   const fallback = hardCoded ?? json[0] ?? null;

        //   if (fallback) {
        //     const payload = {
        //       id: fallback.id,
        //       title: fallback.title.rendered,
        //       slug: fallback.slug,
        //     };
        //     cacheLocation(payload);
        //     state.selectedLocation.value = payload;
        //   }
        // }
      })
      .finally(() => {
        state.isFetchingRestaurants.value = false;
      });
  }

  const mobileReserveButtons = document.querySelectorAll(".mx-mobile-reserve");
  mobileReserveButtons.forEach((b) => {
    b.querySelector(".moxies-button").addEventListener("click", () => {
      state.selectedRid.value = b.id;
    });
  });
}

document.addEventListener("readystatechange", main);

// ─── Web Components ───────────────────────────────────────────────────────────

/**
 * Web Component responsible for bootstrapping the reservation form app.
 * @example <mx-reservation></mx-reservation>
 */
class ReservationLoader extends HTMLElement {
  connectedCallback() {
    this.connectedAt = Date.now();
    createReservationApp(state, this);

    const infoLink = document.querySelector("#reserve-location-link a");
    /** @type {HTMLAnchorElement} */
    const groupBookingsLink = document.querySelector("#group-bookings-link a");
    const telLink = document.querySelector("#reserve-tel-link a");
    const selectedRestaurant = state.selectedRestaurant.peek();

    if (infoLink && telLink && selectedRestaurant) {
      infoLink.setAttribute("href", selectedRestaurant.link);
      telLink.setAttribute("href", `tel:${selectedRestaurant.acf.phone_number}`);

      if (groupBookingsLink) {
        if (selectedRestaurant?.acf?.group_bookings_enabled) {
          groupBookingsLink.style.display = "inline-flex";
          groupBookingsLink.setAttribute("href", `${selectedRestaurant.link}group-bookings`);
        } else {
          groupBookingsLink.style.display = "none";
        }
      }
    }
  }

  /**
   * When the component is removed from the DOM, the app state is reset.
   *
   * NOTE: Elementor renders then immediately removes the popover on page load.
   * Quick add/remove cycles (< 500 ms) are ignored to prevent premature resets.
   */
  disconnectedCallback() {
    if (Date.now() - this.connectedAt > 500) {
      state.reservationToken.value = null;
      state.isLocationConfirmed.value = false;
      state.confirmedReservation.value = null;
      state.pendingTimer.value = null;
      state.partySize.value = 2;
      state.selectedRid.value = "";
      state.selectedTime.value = "";
      state.selectedSeating.value = "";
    }
  }
}

customElements.define("mx-reservation", ReservationLoader);

class OrderLoader extends HTMLElement {
  connectedCallback() {
    this.connectedAt = Date.now();
    createOrdersApp(state, this);
  }

  disconnectedCallback() {
    if (Date.now() - this.connectedAt > 500) {
      state.selectedRid.value = "";
      state.isLocationConfirmed.value = false;
    }
  }
}

customElements.define("mx-order", OrderLoader);